/**
 * Homescreen "New" strip (SPEC `notifications` § Homescreen).
 *
 * Both unread kinds ride it — viral alerts and caught new posts — under one heading, because they
 * are one queue: things that happened and have not been looked at.
 *
 * Sits between the Add/Scrape row and Groups, and renders only while something is unread. The
 * section head stays in the 960px column like every other section; the **track** breaks out of it
 * and runs edge to edge, with the first card still lined up with the column (see `notifications.css`
 * — the deck's negative margin and the track's gutter padding are measured off different boxes on
 * purpose).
 *
 * Scrolling is native (wheel, trackpad, drag) with the scrollbar hidden, plus two 56px circular
 * buttons pinned at the viewport edges that advance about one card and disable at the ends.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { NotificationDTO } from '@lw/shared';
import { ChevronLeftIcon, ChevronRightIcon, Spinner } from '../../components';
import { NOTIFICATIONS_PATH } from '../../app/routes';
import { usePostPreview } from '../view/PreviewContext';
import { NotificationCard } from './NotificationCard';
import { notificationPreviewIds, STRIP_TITLE } from './notificationCardModel';
import { useNotificationActions, useUnreadNotifications, VIRAL_STRIP_LIMIT } from './useNotifications';
import './notifications.css';

/** Gap between cards; mirrors `--notif-gap` in the stylesheet. */
const TRACK_GAP_PX = 16;

type EdgeState = { atStart: boolean; atEnd: boolean };

function useTrackEdges(trackRef: React.RefObject<HTMLDivElement | null>, deps: unknown): EdgeState {
  const [edges, setEdges] = useState<EdgeState>({ atStart: true, atEnd: true });

  useEffect(() => {
    const track = trackRef.current;
    if (track === null) return;
    const measure = () => {
      const max = track.scrollWidth - track.clientWidth;
      setEdges({ atStart: track.scrollLeft <= 1, atEnd: track.scrollLeft >= max - 1 });
    };
    measure();
    track.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => {
      track.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [trackRef, deps]);

  return edges;
}

export type NewViralStripProps = { mediaEnabled: boolean };

export function NewViralStrip({ mediaEnabled }: NewViralStripProps) {
  const query = useUnreadNotifications();
  const { openPreview } = usePostPreview();
  const actions = useNotificationActions();
  const trackRef = useRef<HTMLDivElement>(null);

  const notifications: NotificationDTO[] = query.data?.notifications ?? [];
  const unreadTotal = query.data?.unreadTotal ?? 0;
  const edges = useTrackEdges(trackRef, notifications.length);

  const scrollByCard = useCallback((direction: -1 | 1) => {
    const track = trackRef.current;
    if (track === null) return;
    const first = track.firstElementChild;
    const step = (first === null ? 552 : first.getBoundingClientRect().width) + TRACK_GAP_PX;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  // The whole section is the unread list: nothing unread, nothing to say.
  if (unreadTotal === 0) return null;

  // The popup's prev/next walk stays on this route, so only the in-place variant contributes.
  const ids = notificationPreviewIds(notifications);

  return (
    <section className="section viral-strip">
      <div className="section-head">
        <h2 className="section-title">
          {STRIP_TITLE} <span className="faint">{unreadTotal}</span>
        </h2>
        <div className="section-head__actions">
          <button
            type="button"
            className="btn-ghost btn-ghost--sm"
            onClick={actions.markAllRead}
            disabled={actions.markingAll}
          >
            {actions.markingAll ? <Spinner size="sm" /> : null}
            Mark all as read
          </button>
        </div>
      </div>

      <div className="viral-strip__deck">
        <button
          type="button"
          className="viral-strip__scroll viral-strip__scroll--prev"
          aria-label="Scroll left"
          disabled={edges.atStart}
          onClick={() => scrollByCard(-1)}
        >
          <ChevronLeftIcon size={20} />
        </button>

        <div className="viral-strip__track" ref={trackRef}>
          {/* Both unread kinds ride this track as the same card — it branches internally. */}
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              mediaEnabled={mediaEnabled}
              onOpen={(postId) => openPreview(postId, ids)}
              onMarkRead={actions.markRead}
              dismissing={actions.dismissing.has(notification.id)}
            />
          ))}

          {unreadTotal > VIRAL_STRIP_LIMIT ? (
            <Link className="viral-strip__more" to={NOTIFICATIONS_PATH}>
              View all {unreadTotal}
            </Link>
          ) : null}
        </div>

        <button
          type="button"
          className="viral-strip__scroll viral-strip__scroll--next"
          aria-label="Scroll right"
          disabled={edges.atEnd}
          onClick={() => scrollByCard(1)}
        >
          <ChevronRightIcon size={20} />
        </button>
      </div>
    </section>
  );
}

export default NewViralStrip;
